// =================================================================
// Store: Regimen slice (PSD §9.1.3 / TODO Tasks 1.12, 2.14, 3.9, CC.1, CC.2)
// Orchestrates: weight → renal → safety → strategy-dependent regimen → monitoring
// =================================================================
import type { StateCreator } from 'zustand';
import {
  Agent,
  ConfidenceStatus,
  EIEligibility,
  LevelType,
  Strategy,
  type AgentParams,
  type DosingWeightResult,
  type RegimenResult,
  type RenalResult,
  type SafetyAssessment,
} from '@/types';
import {
  computeDosingWeight,
  computeRenal,
  assessSafety,
  assessNephrotoxinRisk,
  computeEmpiricDose,
  computeEIDose,
  classifyHartford,
  classifyAlternative,
  intervalToHours,
  computeTraditionalRegimen,
  predictPeakTrough,
  roundDoseAndRecompute,
  computeMonitoringSchedule,
  computeCKDEPI,
  computeEgfrDiscrepancy,
  applyContextModifiers,
  computePKPD,
  computeSynergyRegimen,
  computeHemodialysisRegimen,
  convertAmikacinLevel,
  scrForCrClCutoff,
  assessRenalTrend,
  computeScrStaleness,
  computeSteadyStateAttainment,
  doseForCmaxMicTarget,
} from '@/engine';
import { BORDERLINE_BAND_ML_MIN } from '@/config/constants';
import { debounce } from '@/utils/debounce';
import type { StoreState, RegimenSlice } from './types';

export const createRegimenSlice: StateCreator<StoreState, [], [], RegimenSlice> = (set, get) => {
  // Debounced computeAll (300ms) — single shared timer via closure.
  const debouncedComputeAll = debounce(() => get().computeAll(), 300);

  return {
    dosingWeight: null,
    renal: null,
    regimen: null,
    safety: null,
    doseRoundingEnabled: true,
    micValue: null,
    micSource: 'assumed',
    contraindicationOverrides: {},
    lastEngineError: null,

    scheduleComputeAll: () => debouncedComputeAll(),

    computeAll: () => {
      const state = get();
      const { patient, strategyConfig, settings, agentParams, hartfordLevel } = state;
      if (!patient) {
        set({ dosingWeight: null, renal: null, regimen: null, safety: null });
        return;
      }

      const protocol = settings.protocol;
      const infusionHr = strategyConfig.infusionDurationMin / 60;

      // 1. Dosing weight
      const override =
        state.dosingWeight?.override && state.dosingWeight.overrideWeightKg
          ? {
              weightKg: state.dosingWeight.overrideWeightKg,
              reason: state.dosingWeight.overrideReason ?? '',
            }
          : undefined;
      const dwRes = computeDosingWeight(patient, override);
      if (!dwRes.ok) {
        set({ lastEngineError: dwRes.error, dosingWeight: null });
        return;
      }
      const dosingWeight: DosingWeightResult = dwRes.value;

      // 2. Renal
      const renalRes = computeRenal(patient, dosingWeight.selectedWeightKg, protocol, BORDERLINE_BAND_ML_MIN);
      if (!renalRes.ok) {
        set({ lastEngineError: renalRes.error, dosingWeight });
        return;
      }
      let renal: RenalResult = renalRes.value;
      // CKD-EPI eGFR (Phase 2) — always compute; displayed when enabled.
      const egfr = computeCKDEPI(patient.ageYears, patient.sex, patient.scrMgDl);
      const discrepancy = computeEgfrDiscrepancy(renal.crClMlMin, egfr);
      renal = {
        ...renal,
        egfrMlMin173: egfr,
        egfrDiscrepancyNote: discrepancy.note,
      };

      // Emergent FE-E5/E6/E7 — renal trend, staleness, borderline SCr thresholds
      const trend = assessRenalTrend(
        patient.scrMgDl,
        patient.previousScrMgDl,
        patient.previousScrTimestamp,
        patient.scrTimestamp,
      );
      const staleness = computeScrStaleness(patient.scrTimestamp, strategyConfig.infusionStartISO);
      renal = {
        ...renal,
        trendDirection: trend.direction,
        trendDeltaMgDl: trend.deltaMgDl,
        scrAgeHours: staleness.ageHours,
        scrStale: staleness.stale,
      };
      // Only surface SCr threshold solver when EI eligibility is borderline/ineligible/contraindicated
      if (renal.eiEligibility !== EIEligibility.Eligible) {
        const thresholds: { cutoff: number; scr: number }[] = [];
        for (const cutoff of [60, 40, 20]) {
          const t = scrForCrClCutoff(patient.ageYears, patient.sex, dosingWeight.selectedWeightKg, cutoff);
          if (t.ok) thresholds.push({ cutoff, scr: t.value });
        }
        if (thresholds.length) renal = { ...renal, scrThresholds: thresholds };
      }

      // Context modifiers (Phase 2)
      const baseVdLkg = agentParams.vdLkg;
      const context = applyContextModifiers(patient, baseVdLkg);
      const effectiveVdLkg = context.adjustedVdLkg ?? baseVdLkg;

      // 3. Safety — pass real toxicity inputs (FE-E1)
      let durationDays = 1;
      if (strategyConfig.infusionStartISO) {
        const startMs = Date.parse(strategyConfig.infusionStartISO);
        if (!Number.isNaN(startMs)) {
          durationDays = Math.max(1, Math.ceil((Date.now() - startMs) / 864e5));
        }
      } else {
        durationDays = Math.max(1, state.sessions.length);
      }
      const currentPatientId = state.currentSessionId
        ? state.sessions.find((s) => s.id === state.currentSessionId)?.patientId
        : undefined;
      const sessionsForPatient = currentPatientId
        ? state.sessions.filter((s) => s.patientId === currentPatientId)
        : state.sessions;
      const cumulativeDoseMg = sessionsForPatient.reduce(
        (sum, s) => sum + (s.regimen.maintenanceDoseMg ?? s.regimen.empiricDoseMg),
        0,
      );
      const supratherapeuticTroughs = state.levels.filter(
        (l) => l.type === LevelType.Trough && l.valueMg > agentParams.targetTroughRange[1],
      ).length;

      let safety: SafetyAssessment = assessSafety(
        patient,
        dosingWeight,
        renal,
        strategyConfig,
        protocol,
        state.contraindicationOverrides,
        durationDays,
        cumulativeDoseMg,
        supratherapeuticTroughs,
      );
      // Surface context warnings + ARC reroute (FE-E4)
      safety = {
        ...safety,
        contextWarnings: context.warnings,
        suggestedReroute: context.pathwayReroute ?? undefined,
      };

      // 4. Regimen — strategy-dependent
      const kel = renal.populationKelPerHr;
      const vdL = effectiveVdLkg * dosingWeight.selectedWeightKg;

      let regimen: RegimenResult | null = null;

      // Empiric dose (pre-level) — always computed.
      const empiric = computeEmpiricDose(
        agentParams,
        dosingWeight.selectedWeightKg,
        renal.crClMlMin,
        strategyConfig.strategy,
        patient.indication,
      );
      if (!empiric.ok) {
        set({ lastEngineError: empiric.error, dosingWeight, renal, safety });
        return;
      }

      let confidence: ConfidenceStatus = ConfidenceStatus.Empiric;

      if (strategyConfig.strategy === Strategy.ExtendedInterval) {
        const eiDose = computeEIDose(agentParams.eiDoseMgKg, dosingWeight.selectedWeightKg);
        let eiInterval: RegimenResult['eiInterval'] = undefined;
        let eiIntervalReason: string | undefined;
        const nomogramLevelUsed: RegimenResult['nomogramLevelUsed'] = undefined;
        let nomogramLevelInWindow: boolean | undefined;

        if (safety.eiBlocked) {
          eiIntervalReason = safety.eiBlockReasons.join('; ');
        } else if (hartfordLevel) {
          // Amikacin conversion before nomogram lookup.
          const nomoLevel =
            agentParams.agent === Agent.Amikacin
              ? convertAmikacinLevel(hartfordLevel.valueMg)
              : hartfordLevel.valueMg;
          const cls =
            strategyConfig.nomogramSource === 'hartford'
              ? classifyHartford(nomoLevel, hartfordLevel.drawTimeHr)
              : classifyAlternative(
                  strategyConfig.nomogramSource,
                  nomoLevel,
                  hartfordLevel.drawTimeHr,
                );
          if (cls.ok) {
            eiInterval = cls.value.interval ?? undefined;
            eiIntervalReason = cls.value.reason;
            nomogramLevelInWindow = cls.value.inWindow;
            if (cls.value.inWindow) confidence = ConfidenceStatus.LevelInformed;
          }
        }

        // Dose rounding
        let roundedDose: number | undefined;
        let roundedPeak: number | undefined;
        let roundedTrough: number | undefined;
        let roundedFloored: boolean | undefined;
        let roundedCapped: boolean | undefined;
        if (state.doseRoundingEnabled) {
          const rounded = roundDoseAndRecompute(
            eiDose,
            agentParams.roundingIncrementMg,
            agentParams.eiDoseMinMgKg * dosingWeight.selectedWeightKg,
            agentParams.eiDoseMaxMgKg * dosingWeight.selectedWeightKg,
            kel,
            vdL,
            intervalToHours(eiInterval ?? null) || empiric.value.intervalHr || 24,
            infusionHr,
          );
          if (rounded.ok) {
            roundedDose = rounded.value.roundedDoseMg;
            roundedPeak = rounded.value.roundedPeak;
            roundedTrough = rounded.value.roundedTrough;
            roundedFloored = rounded.value.flooredToMin;
            roundedCapped = rounded.value.cappedAtMax;
          }
        }

        // PK/PD if MIC present
        let auc24: number | undefined;
        let aucMic: number | undefined;
        let cmaxMic: number | undefined;
        let cmaxAttainmentDoseMg: number | undefined;
        const activeIntervalHr = intervalToHours(eiInterval ?? null) || 24;
        if (state.micValue && state.micValue > 0) {
          const pkpd = computePKPD(
            roundedDose ?? eiDose,
            kel,
            vdL,
            activeIntervalHr,
            infusionHr,
            state.micValue,
          );
          if (pkpd.ok) {
            auc24 = pkpd.value.auc24;
            aucMic = pkpd.value.aucMic;
            cmaxMic = pkpd.value.cmaxMic;
            // FE-E9: suggest dose needed to hit Cmax/MIC ≥ 8 when target missed
            if (pkpd.value.cmaxMic < 8) {
              const attain = doseForCmaxMicTarget(kel, vdL, activeIntervalHr, infusionHr, state.micValue);
              if (attain.ok) cmaxAttainmentDoseMg = attain.value;
            }
          }
        }

        // FE-E8: steady-state attainment estimate
        const halfLifeHr = Math.log(2) / kel;
        const ssInterval = intervalToHours(eiInterval ?? null) || empiric.value.intervalHr || 24;
        const ssRes = computeSteadyStateAttainment(halfLifeHr, ssInterval);
        const steadyState =
          ssRes.ok && halfLifeHr > 0
            ? {
                ssDoseNumber: ssRes.value.ssDoseNumber,
                ssApproxTimeHr: ssRes.value.ssApproxTimeHr,
                halfLifeHr,
              }
            : undefined;

        regimen = {
          agent: agentParams.agent,
          strategy: strategyConfig.strategy,
          empiricDoseMg: empiric.value.doseMg,
          empiricIntervalHr: empiric.value.intervalHr,
          eiDoseMg: eiDose,
          eiInterval,
          eiIntervalReason,
          nomogramLevelUsed,
          nomogramLevelInWindow,
          roundedDoseMg: roundedDose,
          roundedPredictedPeak: roundedPeak,
          roundedPredictedTrough: roundedTrough,
          roundedDoseFlooredToMin: roundedFloored,
          roundedDoseCappedAtMax: roundedCapped,
          roundedIdealDoseMg: roundedCapped || roundedFloored ? eiDose : undefined,
          steadyState,
          cmaxAttainmentDoseMg,
          auc24,
          aucMic,
          cmaxMic,
          mic: state.micValue ?? undefined,
          micSource: state.micValue ? state.micSource : undefined,
          dosingWeightKg: dosingWeight.selectedWeightKg,
          confidence: safety.eiBlocked ? ConfidenceStatus.RequiresReview : confidence,
          rationaleText: buildRationale(agentParams, strategyConfig, safety, eiInterval),
          overrideApplied: false,
        };
      } else if (strategyConfig.strategy === Strategy.Traditional) {
        const trad = computeTraditionalRegimen(
          kel,
          vdL,
          agentParams.targetPeakMg,
          agentParams.targetTroughMg,
          infusionHr,
        );
        let predictedPeak: number | undefined;
        let predictedTrough: number | undefined;
        if (trad.ok) {
          const pt = predictPeakTrough(trad.value.maintenanceDoseMg, kel, vdL, trad.value.intervalHr, infusionHr);
          if (pt.ok) {
            predictedPeak = pt.value.peak;
            predictedTrough = pt.value.trough;
          }
          regimen = {
            agent: agentParams.agent,
            strategy: strategyConfig.strategy,
            empiricDoseMg: empiric.value.doseMg,
            empiricIntervalHr: empiric.value.intervalHr,
            loadingDoseMg: trad.value.loadingDoseMg,
            maintenanceDoseMg: trad.value.maintenanceDoseMg,
            traditionalIntervalHr: trad.value.intervalHr,
            predictedPeakMg: predictedPeak,
            predictedTroughMg: predictedTrough,
            peakMeetsTarget:
              predictedPeak != null &&
              predictedPeak >= agentParams.targetPeakRange[0] &&
              predictedPeak <= agentParams.targetPeakRange[1],
            troughMeetsTarget:
              predictedTrough != null &&
              predictedTrough >= agentParams.targetTroughRange[0] &&
              predictedTrough <= agentParams.targetTroughRange[1],
            dosingWeightKg: dosingWeight.selectedWeightKg,
            confidence,
            rationaleText: buildRationale(agentParams, strategyConfig, safety, undefined),
            overrideApplied: false,
          };
        }
      } else if (strategyConfig.strategy === Strategy.Synergy) {
        const syn = computeSynergyRegimen(agentParams, dosingWeight.selectedWeightKg, kel, effectiveVdLkg, infusionHr);
        if (syn.ok) {
          regimen = {
            agent: agentParams.agent,
            strategy: strategyConfig.strategy,
            empiricDoseMg: empiric.value.doseMg,
            empiricIntervalHr: empiric.value.intervalHr,
            maintenanceDoseMg: syn.value.doseMg,
            traditionalIntervalHr: syn.value.intervalHr,
            predictedPeakMg: syn.value.predictedPeak,
            predictedTroughMg: syn.value.predictedTrough,
            peakMeetsTarget: syn.value.peakMeetsTarget,
            troughMeetsTarget: syn.value.troughMeetsTarget,
            dosingWeightKg: dosingWeight.selectedWeightKg,
            confidence,
            rationaleText: buildRationale(agentParams, strategyConfig, safety, undefined),
            overrideApplied: false,
          };
        }
      } else if (strategyConfig.strategy === Strategy.Dialysis) {
        const hd = computeHemodialysisRegimen(agentParams, dosingWeight.selectedWeightKg);
        if (hd.ok) {
          regimen = {
            agent: agentParams.agent,
            strategy: strategyConfig.strategy,
            empiricDoseMg: hd.value.doseMg,
            empiricIntervalHr: hd.value.intervalHr,
            dosingWeightKg: dosingWeight.selectedWeightKg,
            confidence: ConfidenceStatus.RequiresReview,
            rationaleText: hd.value.advisory,
            overrideApplied: false,
          };
        }
      }

      // 5. Monitoring — fold nephrotoxin frequency adjustment through (FE-E4)
      const nephrotoxinAdj = assessNephrotoxinRisk(
        patient.concurrentNephrotoxins ?? [],
        durationDays,
      ).frequencyAdjustmentDays;
      const monitoring = computeMonitoringSchedule(
        strategyConfig,
        renal,
        safety,
        strategyConfig.infusionDurationMin,
        nephrotoxinAdj,
      );

      set({
        dosingWeight,
        renal,
        safety,
        regimen,
        monitoring,
        lastEngineError: null,
      });
    },

    overrideDosingWeight: (weightKg, reason) => {
      set((state) => ({
        dosingWeight: state.dosingWeight
          ? {
              ...state.dosingWeight,
              selectedWeightKg: weightKg,
              selectionRule: 'manual-override' as never,
              override: true,
              overrideWeightKg: weightKg,
              overrideReason: reason,
            }
          : null,
      }));
      get().computeAll();
    },

    overrideContraindication: (type, reason) => {
      set((state) => ({
        contraindicationOverrides: { ...state.contraindicationOverrides, [type]: reason },
      }));
      get().computeAll();
    },

    applyDoseRounding: (enabled) => {
      set({ doseRoundingEnabled: enabled });
      get().computeAll();
    },

    setMIC: (mic, source) => {
      set({ micValue: mic, micSource: source });
      get().computeAll();
    },

    overrideRegimen: (dose, interval, reason) => {
      if (!reason.trim()) return; // block without reason (FR-15.5)
      set((state) => {
        if (!state.regimen) return state;
        const regimen: RegimenResult = {
          ...state.regimen,
          empiricDoseMg: dose,
          empiricIntervalHr: interval,
          overrideApplied: true,
          overrideReason: reason,
          confidence: ConfidenceStatus.RequiresReview,
        };
        return { regimen };
      });
      void get().logAuditEvent({ eventType: 'override', newValue: { dose, interval } });
    },
  };
};

function buildRationale(
  agentParams: AgentParams,
  strategy: { strategy: Strategy },
  safety: SafetyAssessment,
  eiInterval: RegimenResult['eiInterval'],
): string {
  const parts: string[] = [];
  parts.push(`${agentParams.agent} via ${strategy.strategy} pathway.`);
  if (safety.eiBlocked) {
    parts.push(`Extended-interval blocked: ${safety.eiBlockReasons.join('; ')}.`);
  } else if (eiInterval) {
    parts.push(`Nomogram interval: ${eiInterval}.`);
  } else {
    parts.push('Population-PK-based estimate; confirm with levels.');
  }
  if (safety.toxicityRiskFactors.length) {
    parts.push(`Toxicity risk factors: ${safety.toxicityRiskFactors.join(', ')}.`);
  }
  return parts.join(' ');
}
